import { useState } from "react";
import { DOCS } from "@/constants/testIds";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { BookOpen, Globe, Terminal, HelpCircle, Wrench, ChevronRight, BadgeCheck } from "lucide-react";
import { motion } from "framer-motion";
import { FADE_UP } from "@/lib/motion";
import { DOC_SECTIONS } from "@/components/docs/DocsSections";
import { NetworkDisclosure, TestTokenNote } from "@/components/common/TestTokenNote";

const sections = [
  { id: "overview", label: "Overview", icon: BookOpen },
  { id: "network", label: "Network Details", icon: Globe },
  { id: "metamask", label: "Add to MetaMask", icon: Wrench },
  { id: "hardhat", label: "Hardhat Config", icon: Terminal },
  { id: "ethers", label: "ethers.js", icon: Terminal },
  { id: "foundry", label: "Foundry", icon: Terminal },
  { id: "faq", label: "FAQ", icon: HelpCircle },
  { id: "listing", label: "Listing Info", icon: BadgeCheck },
];

export default function DocsPage() {
  const [active, setActive] = useState("overview");
  const ActiveSection = DOC_SECTIONS[active];

  return (
    <div data-testid={DOCS.container} className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <motion.div {...FADE_UP}>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-2">
          Connecting to <span className="text-[#D4AF37]">FSC</span>
        </h1>
        <p className="text-zinc-400 text-base mb-4">
          Everything you need to start building on FMI Smart Chain
        </p>
        <div data-testid="docs-disclosure" className="mb-8 p-4 rounded-lg border border-amber-500/30 bg-amber-500/5 max-w-3xl">
          <p className="text-sm font-medium text-amber-300 mb-2">Important</p>
          <NetworkDisclosure testId="docs-network-disclosure" className="text-sm text-zinc-300" />
          <TestTokenNote testId="docs-test-token-note" className="mt-3" />
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Sidebar */}
        <nav data-testid={DOCS.sidebar} className="lg:col-span-3">
          <Card className="border-[#2A2411] bg-[#0A0A0A] sticky top-20">
            <CardContent className="p-3">
              <ScrollArea className="max-h-[calc(100vh-120px)]">
                <div className="space-y-0.5">
                  {sections.map((s) => {
                    const Icon = s.icon;
                    return (
                      <button
                        key={s.id}
                        onClick={() => setActive(s.id)}
                        data-testid={`docs-section-${s.id}`}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left transition-colors ${
                          active === s.id
                            ? "text-[#D4AF37] bg-[#D4AF37]/10"
                            : "text-zinc-400 hover:text-white hover:bg-white/5"
                        }`}
                      >
                        <Icon size={14} />
                        {s.label}
                        {active === s.id && <ChevronRight size={12} className="ml-auto" />}
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </nav>

        {/* Content */}
        <div data-testid={DOCS.content} className="lg:col-span-9 space-y-8">
          <ActiveSection />
        </div>
      </div>
    </div>
  );
}
